import { useEffect, useMemo, useState } from "react";
import { Users, Search, Check } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import { bulkCreateAssignments } from "@/api/documentAssignments";
import { listEmployees } from "@/api/employees";
import type { DocumentTemplate } from "@/types/document";
import type { EmployeeListItem } from "@/types/employee";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  template: DocumentTemplate;
}

export function BulkAssignModal({ isOpen, onClose, template }: Props) {
  const { addToast } = useToast();
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        // Previous version: ``listEmployees(1, 100, { status: "ACTIVE" })``.
        // Two bugs at once:
        //   1. Backend caps per_page at 100 — orgs with > 100 employees
        //      silently lost everyone past row 100, so the picker showed
        //      "not all employees" and you couldn't bulk-assign to them
        //      at all (no way to even type their name in the search,
        //      since the search filters this in-memory list).
        //   2. ``status: "ACTIVE"`` excludes ONBOARDING new hires
        //      (literally the audience for "Anti-Harassment Policy
        //      Acknowledgment") and ON_LEAVE staff (who still owe
        //      compliance docs on return). Only TERMINATED should
        //      truly be filtered out — they're no longer eligible.
        //
        // Fix: paginate through every page (still 100 at a time, the
        // backend's hard cap), then drop TERMINATED rows clientside.
        // We bail out after MAX_PAGES as a safety net against an
        // unexpected schema where total is wrong — at the per_page=100
        // ceiling that's 5,000 employees which is well past any
        // reasonable single-org headcount in this product.
        const MAX_PAGES = 50;
        const PAGE_SIZE = 100;
        const all: EmployeeListItem[] = [];
        for (let page = 1; page <= MAX_PAGES; page++) {
          const resp = await listEmployees(page, PAGE_SIZE);
          if (cancelled) return;
          all.push(...resp.items);
          // Pagination contract: ``items.length < PAGE_SIZE`` means
          // this was the last page. We don't rely on ``resp.total`` —
          // a stale total would either fetch forever or stop short,
          // and the length check is self-correcting.
          if (resp.items.length < PAGE_SIZE) break;
        }
        if (cancelled) return;
        // Exclude TERMINATED — they shouldn't receive new assignments.
        // Everyone else (ACTIVE, INACTIVE, ON_LEAVE) is assignable;
        // ON_LEAVE in particular needs to show up so HR can queue
        // compliance docs for return-from-leave.
        const assignable = all.filter(
          (e) => String(e.status).toUpperCase() !== "TERMINATED",
        );
        setEmployees(assignable);
      } catch {
        if (!cancelled) addToast("error", "Failed to load employees");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, addToast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        e.first_name.toLowerCase().includes(q) ||
        e.last_name.toLowerCase().includes(q) ||
        (e.work_email ?? "").toLowerCase().includes(q),
    );
  }, [employees, search]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectAll = () => {
    setSelected(new Set(filtered.map((e) => e.id)));
  };

  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    if (selected.size === 0) return;
    setIsSubmitting(true);
    try {
      // IMPORTANT: this MUST go through ``/document-assignments/bulk``
      // (which writes ``DocumentAssignment`` rows) — NOT the legacy
      // ``/documents/bulk-assign`` endpoint, which writes a different
      // ``Document`` table the employee-facing "My Documents" page
      // doesn't read. Using the wrong endpoint means the assignment
      // succeeds silently on the admin side but the employee never
      // sees the document on their dashboard.
      const result = await bulkCreateAssignments({
        template_ids: [template.id],
        employee_ids: Array.from(selected),
      });
      const created = result.created;
      const skipped = result.skipped_existing;
      if (created === 0 && skipped > 0) {
        addToast(
          "info",
          `${skipped} already had this document — nothing new to assign.`,
        );
      } else if (skipped > 0) {
        addToast(
          "success",
          `Assigned ${created} (skipped ${skipped} already-assigned)`,
        );
      } else {
        addToast("success", `Assigned to ${created} employee${created === 1 ? "" : "s"}`);
      }
      onClose();
      setSelected(new Set());
    } catch (err) {
      // Surface the server's message instead of the old opaque
      // "Bulk assign failed" — that toast made it impossible to tell
      // a transient 500 from a real input problem (already-assigned,
      // missing employee, validation error). Axios stashes the
      // FastAPI ``detail`` at ``err.response.data.detail``; fall
      // through to the generic message if the shape doesn't match.
      const resp = (err as { response?: { data?: { detail?: unknown } } })
        ?.response;
      const detail = resp?.data?.detail;
      let msg = "Bulk assign failed";
      if (typeof detail === "string" && detail) {
        msg = detail;
      } else if (Array.isArray(detail)) {
        const first = detail[0] as { msg?: string } | undefined;
        if (first?.msg) msg = first.msg;
      } else if (err instanceof Error && err.message) {
        msg = err.message;
      }
      addToast("error", msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Bulk assign "${template.name}"`}
      size="lg"
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
            <Input
              placeholder="Search employees..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={selectAll}>
            Select all
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll}>
            Clear
          </Button>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border/50 border border-border rounded-lg">
          {filtered.length === 0 ? (
            <p className="text-sm text-text-muted p-4 text-center">
              No employees match.
            </p>
          ) : (
            filtered.map((e) => (
              <label
                key={e.id}
                className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-surface/50"
              >
                <input
                  type="checkbox"
                  className="rounded border-border text-accent"
                  checked={selected.has(e.id)}
                  onChange={() => toggle(e.id)}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {e.first_name} {e.last_name}
                  </p>
                  <p className="text-xs text-text-muted truncate">
                    {e.work_email} · {e.title}
                  </p>
                </div>
              </label>
            ))
          )}
        </div>
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Users className="h-4 w-4" />
            <Badge variant="info">{selected.size} selected</Badge>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              isLoading={isSubmitting}
              disabled={selected.size === 0}
            >
              <Check className="h-4 w-4" />
              Assign
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
